import React, { FC, useState } from 'react';
// Payment step disabled — the charge mutation/price query hooks and their supporting imports are commented out.
// import { useMutation } from 'react-relay';
import { stringFormat, T, toLocalID } from 'src/shared/helpers';
import { EcommerceId } from 'src/shared/enums';
import styled from 'styled-components';
import { ButtonContainer } from '../../../shared/connectDialogStyle';
// Payment step disabled — Center/Spinner were only used by the price-summary Suspense fallback.
import { PrimaryButton } from '../../widgets/buttons';
import { Dialog, DialogTitle } from '../../widgets/dialog';
import Stack from '../../widgets/stack/stack';
// Payment step disabled — showErrorNotification was only used by the charge error handlers.
// import { showErrorNotification } from '../../widgets/notificationToaster';
import { callZendeskApi } from 'src/shared/zendeskApi';
import { ZTooltip } from '../../widgets/ztooltip';
// Payment step disabled — generated types for the commented-out charge/price operations.
// import { switchSalesChannelDialogCreateQuickChargeMutation } from './__generated__/switchSalesChannelDialogCreateQuickChargeMutation.graphql';
import { canSwitchSalesChannel } from './helpers';
// Payment step disabled — SwitchProgressDialog was only used by the "Processing Payment..." screen.
// import SwitchProgressDialog from './switch/switchProgressDialog';
import {
    BlockingProductsAlerts,
    LegacyPlanNotSupportedAlert,
    MigrationOnlyAlerts,
    ProductLimitReachedAlert,
    ProductLimitReachedSimpleAlert,
    ScalePlanRequiredAlert
} from './switch/switchSalesChannelDialogAlerts';
import { SellerPlanCheckInfo } from './switch/switchTypes';
import { SwitchAcknowledgeFooter } from './switch/switchAcknowledgeFooter';
import { SwitchDialogWindow } from './switch/switchDialogWindow';
import { SwitchReviewDuringTheSwitch, SwitchReviewNotices, SwitchReviewOverview } from './switch/switchReviewSections';
import { applyReviewPreviewCase, ReviewPreviewCase, ReviewPreviewSwitcher } from './switch/switchReviewPreviewSwitcher';
import { getSwitchWizardStepCount, SwitchWizardStep, SwitchWizardStepIndicator } from './switch/switchWizardSteps';

interface SwitchSalesChannelDialogProps {
    secondarySalesChannelID: string;
    secondaryEcommerceName: string;
    secondarySalesChannelName: string;
    secondaryEcommerceID?: number | null;
    secondaryCurrencyID?: number | null;
    secondaryCurrencyCode?: string | null;
    masterSalesChannelID: string;
    masterEcommerceName: string;
    masterSalesChannelName: string;
    masterEcommerceID?: number | null;
    masterCurrencyID?: number | null;
    masterCurrencyCode?: string | null;
    planCheck?: SellerPlanCheckInfo;
    configuratorModelsCount?: number;
    hasPodProducts?: boolean;
    onClosed?: () => void;
    onPaymentSuccess?: (
        salesChannelID: string,
        masterSalesChannelID: string,
        isShopify: boolean,
        shopifyChargeID: string | null,
        hasAcknowledgedInfo: boolean
    ) => void;
}

const DialogCaption = styled.p`
    font-size: 13px;
    line-height: 18px;
    color: #6b6b6b;
    margin: -16px 0 20px;
`;

// Slightly larger than the shared 14px DialogTitle, without changing it app-wide.
const SwitchDialogTitle = styled(DialogTitle)`
    font-size: 16px;
`;

/** Fills the fixed-height window with the footer at the bottom. Only the content area scrolls, so the footer stays in view. */
const ReviewLayout = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
`;

const WizardContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const StepContentArea = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
`;

// The review is sized to fit the window even with every notice shown. Vertical scroll is only a fallback for very short
// screens, so nothing is ever clipped.
const ReviewContentArea = styled(StepContentArea)`
    overflow-x: hidden;
`;

const DialogContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;
`;

const TooltipTarget = styled.span`
    display: inline-block;
`;

/* Payment step disabled — the charge mutation, price query, and the price-summary UI are commented out.
 */
/** First matching reason wins, mirroring the original nested ternary. Kept as an if-chain so each T._ stays lazy. */
const getProceedTooltip = ({
    hasPodProducts,
    hasConfiguratorProducts,
    isProductLimitReached
}: {
    hasPodProducts: boolean;
    hasConfiguratorProducts: boolean;
    isProductLimitReached: boolean;
}): string => {
    if (hasPodProducts) return T._('You must remove all Print-on-Demand products from your account before proceeding.', 'Admin');
    if (hasConfiguratorProducts) return T._('You must remove all 3D Product Configurator products before proceeding.', 'Admin');
    if (isProductLimitReached) return T._('You must reduce your published products below the plan limit before proceeding.', 'Admin');
    return '';
};

const ProceedWithPaymentButton: FC<{
    isCreatingCharge: boolean;
    isPriceLoading: boolean;
    isProductLimitReached: boolean;
    hasConfiguratorProducts: boolean;
    hasPodProducts: boolean;
    onClick: () => void;
}> = ({ isCreatingCharge, isPriceLoading, isProductLimitReached, hasConfiguratorProducts, hasPodProducts, onClick }) => (
    <ZTooltip position='top' title={getProceedTooltip({ hasPodProducts, hasConfiguratorProducts, isProductLimitReached })}>
        <TooltipTarget>
            <PrimaryButton
                onClick={onClick}
                disabled={isCreatingCharge || isPriceLoading || isProductLimitReached || hasConfiguratorProducts || hasPodProducts}
            >
                {isCreatingCharge ? T._('Processing...', 'Admin') : T._('Proceed', 'Admin')}
            </PrimaryButton>
        </TooltipTarget>
    </ZTooltip>
);

const ContactSupportButton: FC<{ onClick: () => void }> = ({ onClick }) => (
    <ButtonContainer>
        <PrimaryButton onClick={onClick}>{T._('Contact Support', 'Admin')}</PrimaryButton>
    </ButtonContainer>
);

const ManualMigrationActions = styled(Stack)`
    align-items: center;
    justify-content: flex-end;
`;

const SwitchSalesChannelDialog: FC<SwitchSalesChannelDialogProps> = ({
    secondarySalesChannelID,
    secondaryEcommerceName,
    secondarySalesChannelName,
    secondaryEcommerceID,
    secondaryCurrencyID,
    secondaryCurrencyCode,
    masterSalesChannelID,
    masterEcommerceName,
    masterSalesChannelName,
    masterEcommerceID,
    masterCurrencyID,
    masterCurrencyCode,
    planCheck,
    configuratorModelsCount,
    hasPodProducts,
    onClosed,
    onPaymentSuccess
}) => {
    // Payment step disabled — the charge mutation and its progress/price state are commented out.
    // const [createQuickCharge, isCreatingCharge] = useMutation<switchSalesChannelDialogCreateQuickChargeMutation>(createQuickChargeMutation);
    // const [isRedirecting, setIsRedirecting] = useState(false);
    const [hasAcknowledgedInfo, setHasAcknowledgedInfo] = useState(false);
    // const [switchPrice, setSwitchPrice] = useState<number | null>(null);

    const isShopify = masterEcommerceName === 'SHOPIFY' || secondaryEcommerceName === 'SHOPIFY';
    const bothAreShopify = masterEcommerceName === 'SHOPIFY' && secondaryEcommerceName === 'SHOPIFY';
    const masterIsApi = masterEcommerceID === EcommerceId.API;
    const secondaryIsApi = secondaryEcommerceID === EcommerceId.API;
    const hasDifferentIntegrationTypes = masterIsApi !== secondaryIsApi;
    const requiresPaymentMethodSetup = hasDifferentIntegrationTypes;
    const hasCurrencyMismatch = !!masterCurrencyCode && !!secondaryCurrencyCode && masterCurrencyCode !== secondaryCurrencyCode;
    const requiresCustomPlanSubscription =
        !bothAreShopify &&
        (hasDifferentIntegrationTypes || (hasCurrencyMismatch && ((masterIsApi && secondaryIsApi) || (!isShopify && !masterIsApi && !secondaryIsApi))));
    const stepCount = getSwitchWizardStepCount(
        { ecommerceName: masterEcommerceName, currencyID: masterCurrencyID },
        { ecommerceName: secondaryEcommerceName, currencyID: secondaryCurrencyID }
    );

    // TEMP: preview switcher — remove before merging, and pass the real flags to the review sections again.
    const [previewCase, setPreviewCase] = useState<ReviewPreviewCase>('real');
    const noticeFlags = applyReviewPreviewCase(previewCase, {
        masterCurrencyCode,
        secondaryCurrencyCode,
        requiresShopifyApproval: bothAreShopify,
        requiresCustomPlanSubscription,
        requiresPaymentMethodSetup,
        hasCurrencyMismatch
    });

    // Payment step disabled — instead of creating a charge, the switch skips straight to the next step (Subscription)
    // for both Shopify and non-Shopify. onPaymentSuccess proceeds directly; see useSalesChannelSwitch.proceedToSubscriptionStep.
    const handleConfirm = () => {
        onClosed?.();
        const secondaryLocalID = toLocalID(secondarySalesChannelID);
        const masterLocalID = toLocalID(masterSalesChannelID);
        if (secondaryLocalID !== null && masterLocalID !== null) {
            onPaymentSuccess?.(secondaryLocalID.toString(), masterLocalID.toString(), isShopify, null, hasAcknowledgedInfo);
        }
    };

    /* Payment step disabled — original charge flow (Shopify redirect + non-Shopify direct charge) kept for reference.
    const handleConfirmWithPayment = () => {
        if (switchPrice === null) return;

        if (isShopify) {
            // Shopify charge flow - create the charge and redirect to Shopify
            const returnUrl = `${window.location.origin}/admin/sales-channels?paymentSuccess=true&salesChannelID=${toLocalID(
                secondarySalesChannelID
            )}&masterSalesChannelID=${toLocalID(masterSalesChannelID)}`;

            createQuickCharge({
                variables: {
                    input: {
                        isForTrial: false,
                        price: switchPrice,
                        description: `Sales Channel Switch - Master Integration Transfer`,
                        returnUrlFromApproval: returnUrl
                    }
                },
                onCompleted: (response) => {
                    // response.createQuickCharge contains the Shopify charge confirmation URL
                    if (response.createQuickCharge) {
                        setIsRedirecting(true);
                        window.location.href = response.createQuickCharge;
                    } else {
                        showErrorNotification({
                            message: T._('Failed to get payment URL. Please try again.', 'Admin')
                        });
                    }
                },
                onError: (error) => {
                    const message = (error as any).errors?.[0]?.message || error.message;
                    showErrorNotification({ message });
                }
            });
        } else {
            // Non-Shopify flow: execute payment directly via createQuickCharge
            createQuickCharge({
                variables: {
                    input: {
                        isForTrial: false,
                        price: switchPrice,
                        description: `Sales Channel Switch - Master Integration Transfer`,
                        returnUrlFromApproval: '' // Non-Shopify: no redirect needed
                    }
                },
                onCompleted: (_response) => {
                    onClosed?.();
                    const secondaryLocalID = toLocalID(secondarySalesChannelID);
                    const masterLocalID = toLocalID(masterSalesChannelID);
                    if (secondaryLocalID !== null && masterLocalID !== null) {
                        onPaymentSuccess?.(secondaryLocalID.toString(), masterLocalID.toString(), false, null, hasAcknowledgedInfo);
                    }
                },
                onError: (error) => {
                    const message = (error as any).errors?.[0]?.message || error.message;
                    showErrorNotification({ message });
                }
            });
        }
    };
    */

    const handleContactSupport = () => {
        // Close the dialog first
        onClosed?.();

        // Open Zendesk messenger
        callZendeskApi('messenger', 'open');
    };

    const canSwitch = canSwitchSalesChannel(secondarySalesChannelID, secondaryEcommerceName, masterEcommerceName);
    const hasConfiguratorProducts = (configuratorModelsCount ?? 0) > 0;
    const hasPodProductsBlocking = hasPodProducts === true;
    const isV1PlanBlocked = planCheck?.isV1Plan === true;
    const isNotV1RequiresScale = !isV1PlanBlocked && planCheck?.maxProductsGrow != null && planCheck.currentProductCount > planCheck.maxProductsGrow;
    const isNotV1ProductLimitReached = !isV1PlanBlocked && (planCheck?.isProductLimitReached ?? false);
    const isBlocked = hasConfiguratorProducts || hasPodProductsBlocking;
    const canProceed = !isV1PlanBlocked && !isBlocked;

    /* Payment step disabled — the "Processing Payment..." progress screen is no longer reachable.
    if (isCreatingCharge || isRedirecting) {
        return (
            <SwitchProgressDialog
                showCloseButton={false}
                step={SwitchWizardStep.Payment}
                title={T._('Processing Payment...', 'Admin')}
                note={isShopify ? T._('You will be redirected to the payment page shortly.', 'Admin') : ''}
                secondaryNote={T._('Please do not close this window.', 'Admin')}
            />
        );
    }
    */

    // The seller cannot switch automatically — only a manual migration is possible.
    if (!canSwitch) {
        return (
            <Dialog windowDecorator={SwitchDialogWindow}>
                <SwitchDialogTitle>{T._('Switch unavailable', 'Admin')}</SwitchDialogTitle>
                <DialogCaption>{T._('The selected sales channel cannot become the Master Integration automatically.', 'Admin')}</DialogCaption>
                <StepContentArea>
                    <DialogContent>
                        <MigrationOnlyAlerts />
                        <ManualMigrationActions $horizontal $gap='8px'>
                            <PrimaryButton onClick={handleContactSupport}>{T._('Contact Support', 'Admin')}</PrimaryButton>
                        </ManualMigrationActions>
                    </DialogContent>
                </StepContentArea>
            </Dialog>
        );
    }

    return (
        <Dialog windowDecorator={SwitchDialogWindow} onClose={onClosed}>
            <ReviewLayout>
                <SwitchWizardStepIndicator step={SwitchWizardStep.Review} stepCount={stepCount} />
                <SwitchDialogTitle>{T._('Switch overview', 'Admin')}</SwitchDialogTitle>
                <DialogCaption>{stringFormat(T._('Set {0} as Master Integration', 'Admin'), secondarySalesChannelName)}</DialogCaption>
                <WizardContainer>
                    <ReviewContentArea>
                        <DialogContent>
                            {isV1PlanBlocked ? (
                                // Legacy V1 plans cannot switch at all.
                                <>
                                    <LegacyPlanNotSupportedAlert />
                                    <ContactSupportButton onClick={handleContactSupport} />
                                </>
                            ) : isBlocked ? (
                                // POD or 3D Configurator products must be removed first. Note this branch never renders
                                // SalesChannelPriceSummary, so switchPrice stays null and the button stays disabled.
                                <>
                                    <BlockingProductsAlerts hasPodProducts={hasPodProductsBlocking} hasConfiguratorProducts={hasConfiguratorProducts} />
                                    <ButtonContainer>
                                        <ProceedWithPaymentButton
                                            isCreatingCharge={false}
                                            isPriceLoading={false}
                                            isProductLimitReached={planCheck?.isProductLimitReached ?? false}
                                            hasConfiguratorProducts={hasConfiguratorProducts}
                                            hasPodProducts={hasPodProductsBlocking}
                                            onClick={handleConfirm}
                                        />
                                    </ButtonContainer>
                                </>
                            ) : (
                                // The switch can proceed. The plan-check alerts below are not mutually exclusive.
                                <>
                                    {isNotV1RequiresScale && <ScalePlanRequiredAlert />}
                                    {isNotV1ProductLimitReached && planCheck && <ProductLimitReachedAlert planCheck={planCheck} />}
                                    {!isNotV1RequiresScale && !isNotV1ProductLimitReached && planCheck?.isProductLimitReached && (
                                        <ProductLimitReachedSimpleAlert planCheck={planCheck} />
                                    )}

                                    {/* Payment step disabled — the "Payment & Pricing" summary is no longer shown.
                        <NotificationBanner
                            type='INFO'
                            title={T._('Payment & Pricing', 'Admin')}
                            content={
                                <Suspense
                                    fallback={
                                        <Center>
                                            <Spinner />
                                        </Center>
                                    }
                                >
                                    <FinancialPreview salesChannelID={secondarySalesChannelID} />
                                </Suspense>
                            }
                        />
                        */}

                                    {/* The currencies are shown only alongside the currency notice they explain. */}
                                    <SwitchReviewOverview
                                        masterSalesChannelName={masterSalesChannelName}
                                        secondarySalesChannelName={secondarySalesChannelName}
                                        masterCurrencyCode={noticeFlags.hasCurrencyMismatch ? noticeFlags.masterCurrencyCode : null}
                                        secondaryCurrencyCode={noticeFlags.hasCurrencyMismatch ? noticeFlags.secondaryCurrencyCode : null}
                                    />
                                    <SwitchReviewNotices
                                        masterSalesChannelName={masterSalesChannelName}
                                        secondarySalesChannelName={secondarySalesChannelName}
                                        {...noticeFlags}
                                    />
                                    <SwitchReviewDuringTheSwitch />
                                </>
                            )}
                        </DialogContent>
                    </ReviewContentArea>
                    {canProceed && (
                        <SwitchAcknowledgeFooter
                            checked={hasAcknowledgedInfo}
                            onChange={setHasAcknowledgedInfo}
                            onProceed={handleConfirm}
                            proceedDisabled={planCheck?.isProductLimitReached ?? false}
                            proceedTooltip={getProceedTooltip({
                                hasPodProducts: false,
                                hasConfiguratorProducts: false,
                                isProductLimitReached: planCheck?.isProductLimitReached ?? false
                            })}
                        />
                    )}
                </WizardContainer>
            </ReviewLayout>
            {/* TEMP: preview switcher — remove before merging. */}
            <ReviewPreviewSwitcher value={previewCase} onChange={setPreviewCase} />
        </Dialog>
    );
};

export default SwitchSalesChannelDialog;
